import { apiFetch } from "../lib/apiFetch";
import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { auth } from "../lib/firebase";
import {
  Phone,
  PhoneCall,
  Plus,
  Pencil,
  Trash2,
  Save,
  Loader2,
  BrainCircuit,
  FileText,
  Clock,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertCircle,
  User,
} from "lucide-react";
import { cn } from "../lib/utils";

interface CallAgenda {
  id: string;
  name: string;
  description: string;
  systemPrompt: string;
  greetingPrompt: string;
  useKnowledgeBase: boolean;
  ownerId: string;
  createdAt: number;
  updatedAt: number;
}

interface CallLog {
  id: string;
  callSid: string;
  leadId: string;
  leadName?: string;
  phoneNumber: string;
  agendaId: string;
  agendaName: string;
  status: string;
  duration?: number;
  startedAt: number;
  endedAt?: number;
}

interface Lead {
  id: string;
  phoneNumber: string;
  name?: string;
  stage?: string;
}

const SALES_TRIAL_AGENDA_TEMPLATE: Omit<CallAgenda, "id" | "ownerId" | "createdAt" | "updatedAt"> = {
  name: "Sales Trial Conversion",
  description: "AI sales agent for 5-6 day trial → paid conversion. Calls to onboard, support, and close.",
  useKnowledgeBase: true,
  greetingPrompt:
    "Greet the lead warmly in Hindi or English based on their name. Introduce yourself as a helpful AI assistant from the company. Ask how their trial is going and if they have downloaded the app yet. Keep it warm, short, and conversational.",
  systemPrompt: `You are a friendly, helpful AI sales representative on a phone call. Your goal is to guide the lead through their free trial and help them get value, then close the sale by day 5-6.

Your tasks by call day:
- Day 1-2 (Onboarding): Confirm they have downloaded the app. Walk them through the key features. Solve any setup issues. Leave them excited.
- Day 3-4 (Value Check-in): Ask how they are using it. Share a quick tip or use case relevant to their business. Handle objections warmly.
- Day 5-6 (Conversion): Summarize the value they have experienced. Explain the pricing plans clearly. Handle objections (cost, timing, features). Ask for the sale directly. If they agree, guide them to the payment link.

Rules:
- Speak naturally, not like a robot. Be warm and conversational.
- If the lead speaks Hindi or Hinglish, respond in simple Hinglish (Roman script only, no Devanagari).
- If the lead speaks English, respond in English.
- Keep each response short — 2-3 sentences max.
- Never use markdown, bullet points, or formatting while speaking.
- If the lead says they are busy, politely schedule a callback and end the call.
- If they are ready to pay, say: "I am so glad to hear that! I will send you the payment link on WhatsApp right now."
- When the conversation is fully complete and the lead confirms no more help needed, end with __END_CALL__.`,
};

const QUALIFICATION_AGENDA_TEMPLATE: Omit<CallAgenda, "id" | "ownerId" | "createdAt" | "updatedAt"> = {
  name: "Lead Qualification",
  description: "Qualify inbound leads using your knowledge base Q&A to determine fit and readiness.",
  useKnowledgeBase: true,
  greetingPrompt:
    "Greet the lead warmly. Introduce yourself as a helpful AI from the company. Tell them you are calling to understand their needs better and see how you can help.",
  systemPrompt: `You are a friendly AI sales qualifier on a phone call. Your job is to understand the lead's needs, qualify them, and set up next steps.

Your tasks:
1. Ask the qualification questions from the knowledge base naturally — one at a time, not like a form.
2. Listen carefully. If they mention a pain point, acknowledge it and dig deeper.
3. Based on their answers, assess if they are a good fit.
4. If qualified: offer a demo or trial. If not a fit: politely explain and suggest alternatives.

Rules:
- Be conversational and warm. Never robotic.
- Match language to the caller: Hindi/Hinglish or English.
- Keep responses under 3 sentences.
- No markdown or formatting while speaking.
- End the call gracefully when done. Say __END_CALL__ when fully complete.`,
};

const FOLLOWUP_AGENDA_TEMPLATE: Omit<CallAgenda, "id" | "ownerId" | "createdAt" | "updatedAt"> = {
  name: "Re-engagement Follow-up",
  description: "Re-engage cold or silent leads to revive interest.",
  useKnowledgeBase: true,
  greetingPrompt:
    "Greet them by name. Introduce yourself. Mention you are reaching out because it has been a while and you wanted to check if they had any questions or if anything changed in their situation.",
  systemPrompt: `You are a warm, friendly AI sales agent doing a re-engagement call. The lead went cold — your job is to understand why and rekindle their interest.

Your approach:
1. Start with genuine curiosity: "How have things been?" or "Did anything change since we last spoke?"
2. If they lost interest: find the real reason — was it price, timing, or they found another solution?
3. Address the real objection directly. If it was price, share any new plans or offers. If timing, offer to schedule for later.
4. If they are ready to re-engage: move to next steps (trial, demo, payment).

Rules:
- Be empathetic, not pushy. Never be aggressive.
- Match the caller's language (Hindi/Hinglish or English).
- Keep responses under 3 sentences.
- No markdown or formatting while speaking.
- Say __END_CALL__ when the conversation is complete.`,
};

const STATUS_COLORS: Record<string, string> = {
  completed: "text-green-600 bg-green-50",
  "in-progress": "text-blue-600 bg-blue-50",
  initiated: "text-indigo-600 bg-indigo-50",
  ringing: "text-yellow-600 bg-yellow-50",
  failed: "text-red-600 bg-red-50",
  "no-answer": "text-slate-600 bg-slate-50",
  busy: "text-orange-600 bg-orange-50",
};

function formatDuration(seconds?: number): string {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Agenda Editor ─────────────────────────────────────────────────────────────

function AgendaEditor({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial?: Partial<CallAgenda>;
  onSave: (data: Omit<CallAgenda, "id" | "ownerId" | "createdAt" | "updatedAt">) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    description: initial?.description || "",
    systemPrompt: initial?.systemPrompt || "",
    greetingPrompt: initial?.greetingPrompt || "",
    useKnowledgeBase: initial?.useKnowledgeBase ?? true,
  });

  return (
    <div className="bg-white border rounded-xl shadow-sm p-6 space-y-4">
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1">Agenda Name</label>
        <input
          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
          placeholder="e.g. Sales Trial Conversion"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1">Description</label>
        <input
          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
          placeholder="Brief description of this call's goal"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1">Opening Greeting Prompt</label>
        <p className="text-xs text-slate-500 mb-1">Tells the AI how to open the call. Keep it short.</p>
        <textarea
          className="w-full h-24 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 resize-none"
          placeholder="Greet the lead warmly. Introduce yourself as..."
          value={form.greetingPrompt}
          onChange={(e) => setForm({ ...form, greetingPrompt: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1">Full System Prompt</label>
        <p className="text-xs text-slate-500 mb-1">Complete instructions for the AI agent during this call.</p>
        <textarea
          className="w-full h-52 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 resize-none font-mono"
          placeholder="You are a friendly AI sales agent..."
          value={form.systemPrompt}
          onChange={(e) => setForm({ ...form, systemPrompt: e.target.value })}
        />
      </div>
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          className="w-4 h-4 accent-green-600"
          checked={form.useKnowledgeBase}
          onChange={(e) => setForm({ ...form, useKnowledgeBase: e.target.checked })}
        />
        <span className="text-sm font-medium text-slate-700">Inject company Knowledge Base into system prompt</span>
      </label>
      <div className="flex gap-2 justify-end pt-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-slate-600 border rounded-lg hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          disabled={!form.name || !form.systemPrompt || saving}
          onClick={() => onSave(form)}
          className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Agenda
        </button>
      </div>
    </div>
  );
}

// ─── Tabs ──────────────────────────────────────────────────────────────────────

type Tab = "leads" | "agendas" | "logs";

export function Calling() {
  const [tab, setTab] = useState<Tab>("agendas");
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedAgendaId, setSelectedAgendaId] = useState<string>("");
  const [callingLeadId, setCallingLeadId] = useState<string | null>(null);
  const [callResult, setCallResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [leadSearch, setLeadSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [editingAgenda, setEditingAgenda] = useState<CallAgenda | "new" | null>(null);
  const queryClient = useQueryClient();

  const ownerId = auth.currentUser?.uid || "";

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(leadSearch), 300);
    return () => clearTimeout(t);
  }, [leadSearch]);

  // ── Leads from SQLite (server-side search, 50-lead limit) ──────────────────
  const { data: leads = [] } = useQuery<Lead[]>({
    queryKey: ["leads", ownerId, debouncedSearch],
    queryFn: async () => {
      if (!ownerId) return [];
      const params = new URLSearchParams({ ownerId, limit: "50" });
      if (debouncedSearch) params.set("search", debouncedSearch);
      const res = await apiFetch(`/api/whatsapp/chats?${params}`);
      if (!res.ok) throw new Error("Failed to fetch leads");
      return res.json();
    },
    enabled: !!ownerId,
  });

  // ── Agendas from SQLite ─────────────────────────────────────────────────────
  const { data: agendas = [], isPending: agendasLoading } = useQuery<CallAgenda[]>({
    queryKey: ["callAgendas", ownerId],
    queryFn: async () => {
      if (!ownerId) return [];
      const res = await apiFetch(`/api/calling/agendas?ownerId=${ownerId}`);
      if (!res.ok) throw new Error("Failed to fetch agendas");
      return res.json();
    },
    enabled: !!ownerId,
  });

  // ── Call logs from SQLite ───────────────────────────────────────────────────
  const { data: logs = [] } = useQuery<CallLog[]>({
    queryKey: ["callLogs", ownerId],
    queryFn: async () => {
      if (!ownerId) return [];
      const res = await apiFetch(`/api/calling/logs?ownerId=${ownerId}`);
      if (!res.ok) throw new Error("Failed to fetch logs");
      return res.json();
    },
    enabled: !!ownerId && tab === "logs",
    refetchInterval: tab === "logs" ? 8000 : false,
  });

  // ── Save agenda (create or update) ─────────────────────────────────────────
  const saveAgendaMutation = useMutation({
    mutationFn: async (data: Omit<CallAgenda, "id" | "ownerId" | "createdAt" | "updatedAt">) => {
      if (editingAgenda && editingAgenda !== "new") {
        const res = await apiFetch(`/api/calling/agendas/${editingAgenda.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...data, ownerId }),
        });
        if (!res.ok) throw new Error("Failed to update agenda");
      } else {
        const res = await apiFetch("/api/calling/agendas", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...data, ownerId }),
        });
        if (!res.ok) throw new Error("Failed to create agenda");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["callAgendas"] });
      setEditingAgenda(null);
    },
  });

  // ── Delete agenda ───────────────────────────────────────────────────────────
  const deleteAgendaMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/api/calling/agendas/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete agenda");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["callAgendas"] }),
  });

  // ── Seed template ───────────────────────────────────────────────────────────
  const seedTemplate = useCallback(
    async (template: Omit<CallAgenda, "id" | "ownerId" | "createdAt" | "updatedAt">) => {
      await apiFetch("/api/calling/agendas", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...template, ownerId }),
      });
      queryClient.invalidateQueries({ queryKey: ["callAgendas"] });
    },
    [ownerId, queryClient]
  );

  // ── Initiate call ───────────────────────────────────────────────────────────
  const handleCall = useCallback(async () => {
    if (!selectedLead || !selectedAgendaId) return;
    setCallingLeadId(selectedLead.id);
    setCallResult(null);
    try {
      const resp = await apiFetch("/api/calling/initiate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leadId: selectedLead.id, agendaId: selectedAgendaId, ownerId }),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        setCallResult({ ok: true, msg: `Call initiated! SID: ${data.callSid}` });
        queryClient.invalidateQueries({ queryKey: ["callLogs"] });
      } else {
        setCallResult({ ok: false, msg: data.error || "Failed to initiate call" });
      }
    } catch {
      setCallResult({ ok: false, msg: "Server error. Check CRM connection." });
    } finally {
      setCallingLeadId(null);
    }
  }, [selectedLead, selectedAgendaId, ownerId, queryClient]);


  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Phone className="w-6 h-6 text-green-600" />
          <h1 className="text-xl font-bold text-slate-900">AI Calling</h1>
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
            Powered by Exotel + Gemini
          </span>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {(["leads", "agendas", "logs"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                "px-4 py-1.5 text-sm font-medium rounded-md transition-all capitalize",
                tab === t
                  ? "bg-white shadow-sm text-slate-900"
                  : "text-slate-500 hover:text-slate-700"
              )}
            >
              {t === "logs" ? "Call Logs" : t === "agendas" ? "Agendas" : "Call Leads"}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">

        {/* ─── Call Leads Tab ─── */}
        {tab === "leads" && (
          <div className="max-w-4xl mx-auto p-6 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Lead Selector */}
              <div className="bg-white border rounded-xl shadow-sm p-5 space-y-3">
                <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  Select Lead
                </h2>
                <input
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500"
                  placeholder="Search by name or phone... (showing top 50)"
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                />
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {leads.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-6">No leads found</p>
                  )}
                  {leads.map((lead) => (
                    <button
                      key={lead.id}
                      onClick={() => setSelectedLead(lead)}
                      className={cn(
                        "w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all",
                        selectedLead?.id === lead.id
                          ? "bg-green-50 border border-green-200 text-green-900"
                          : "hover:bg-slate-50 border border-transparent"
                      )}
                    >
                      <p className="font-medium">{lead.name || "Unnamed"}</p>
                      <p className="text-xs text-slate-500">{lead.phoneNumber}</p>
                      {lead.stage && (
                        <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded mt-1 inline-block">
                          {lead.stage}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Agenda Selector */}
              <div className="bg-white border rounded-xl shadow-sm p-5 space-y-3">
                <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  Select Call Agenda
                </h2>
                {agendas.length === 0 && !agendasLoading ? (
                  <div className="text-center py-6 space-y-3">
                    <p className="text-sm text-slate-500">No agendas yet.</p>
                    <button
                      onClick={() => setTab("agendas")}
                      className="text-sm text-green-600 font-medium hover:underline"
                    >
                      Create an agenda →
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {agendas.map((agenda) => (
                      <button
                        key={agenda.id}
                        onClick={() => setSelectedAgendaId(agenda.id)}
                        className={cn(
                          "w-full text-left px-3 py-3 rounded-lg text-sm border transition-all",
                          selectedAgendaId === agenda.id
                            ? "bg-green-50 border-green-200 text-green-900"
                            : "hover:bg-slate-50 border-slate-100"
                        )}
                      >
                        <p className="font-medium">{agenda.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{agenda.description}</p>
                        {agenda.useKnowledgeBase && (
                          <span className="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded mt-1 inline-flex items-center gap-1">
                            <BrainCircuit className="w-2.5 h-2.5" /> Uses KB
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Call Button */}
            <div className="bg-white border rounded-xl shadow-sm p-5">
              {callResult && (
                <div
                  className={cn(
                    "mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2",
                    callResult.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
                  )}
                >
                  {callResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  {callResult.msg}
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  {selectedLead && selectedAgendaId ? (
                    <>
                      Calling{" "}
                      <span className="font-semibold text-slate-900">
                        {selectedLead.name || selectedLead.phoneNumber}
                      </span>{" "}
                      with agenda{" "}
                      <span className="font-semibold text-slate-900">
                        {agendas.find((a) => a.id === selectedAgendaId)?.name}
                      </span>
                    </>
                  ) : (
                    <span className="text-slate-400">Select a lead and agenda above to start a call</span>
                  )}
                </div>
                <button
                  disabled={!selectedLead || !selectedAgendaId || !!callingLeadId}
                  onClick={handleCall}
                  className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-40 flex items-center gap-2 shadow-sm transition-all"
                >
                  {callingLeadId ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <PhoneCall className="w-4 h-4" />
                  )}
                  {callingLeadId ? "Initiating Call..." : "Start AI Call"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Agendas Tab ─── */}
        {tab === "agendas" && (
          <div className="max-w-3xl mx-auto p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-800">Call Agendas</h2>
              <button
                onClick={() => setEditingAgenda("new")}
                className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" />
                New Agenda
              </button>
            </div>

            {/* Template Seeds */}
            {agendas.length === 0 && !agendasLoading && editingAgenda === null && (
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5 space-y-3">
                <p className="text-sm font-semibold text-indigo-900">Quick start with a template:</p>
                <div className="grid grid-cols-3 gap-3">
                  {[SALES_TRIAL_AGENDA_TEMPLATE, QUALIFICATION_AGENDA_TEMPLATE, FOLLOWUP_AGENDA_TEMPLATE].map(
                    (tpl) => (
                      <button
                        key={tpl.name}
                        onClick={() => seedTemplate(tpl)}
                        className="text-left p-3 bg-white border border-indigo-100 rounded-lg hover:border-indigo-300 transition-all"
                      >
                        <p className="text-sm font-semibold text-indigo-800">{tpl.name}</p>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{tpl.description}</p>
                      </button>
                    )
                  )}
                </div>
              </div>
            )}

            {editingAgenda !== null && (
              <AgendaEditor
                initial={editingAgenda === "new" ? undefined : editingAgenda}
                onSave={(data) => saveAgendaMutation.mutate(data)}
                onCancel={() => setEditingAgenda(null)}
                saving={saveAgendaMutation.isPending}
              />
            )}

            {agendasLoading && agendas.length === 0 && (
              <div className="text-center py-12 text-slate-400 text-sm flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading agendas...
              </div>
            )}

            {agendas.map((agenda) => (
              <div key={agenda.id} className="bg-white border rounded-xl shadow-sm p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900">{agenda.name}</h3>
                      {agenda.useKnowledgeBase && (
                        <span className="text-[10px] bg-indigo-50 text-indigo-600 border border-indigo-100 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                          <BrainCircuit className="w-2.5 h-2.5" /> Uses KB
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{agenda.description}</p>
                    <details className="mt-2 group">
                      <summary className="text-xs text-indigo-600 cursor-pointer hover:underline select-none flex items-center gap-1">
                        View prompts
                        <ChevronDown className="w-3 h-3 group-open:hidden" />
                        <ChevronUp className="w-3 h-3 hidden group-open:block" />
                      </summary>
                      <div className="mt-2 space-y-2 text-xs text-slate-600">
                        <div>
                          <span className="font-semibold text-slate-700">Greeting: </span>
                          {agenda.greetingPrompt}
                        </div>
                        <div>
                          <span className="font-semibold text-slate-700">System: </span>
                          <pre className="whitespace-pre-wrap font-sans leading-relaxed mt-1 text-slate-500 bg-slate-50 rounded p-2 max-h-32 overflow-y-auto">
                            {agenda.systemPrompt}
                          </pre>
                        </div>
                      </div>
                    </details>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => setEditingAgenda(agenda)}
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete "${agenda.name}"?`)) deleteAgendaMutation.mutate(agenda.id);
                      }}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ─── Call Logs Tab ─── */}
        {tab === "logs" && (
          <div className="max-w-5xl mx-auto p-6">
            <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400" />
              Call History
            </h2>
            {logs.length === 0 ? (
              <div className="bg-white border rounded-xl p-12 text-center text-slate-400 text-sm">
                No calls yet. Start a call from the Call Leads tab.
              </div>
            ) : (
              <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      <th className="px-4 py-3">Lead</th>
                      <th className="px-4 py-3">Agenda</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Duration</th>
                      <th className="px-4 py-3">Started</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">{log.leadName || "—"}</p>
                          <p className="text-xs text-slate-500">{log.phoneNumber}</p>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{log.agendaName}</td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium capitalize",
                              STATUS_COLORS[log.status] || "text-slate-600 bg-slate-50"
                            )}
                          >
                            {log.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{formatDuration(log.duration)}</td>
                        <td className="px-4 py-3 text-slate-500">{formatTime(log.startedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
